import { useCallback, useEffect, useRef, useState } from 'react';

import { useProductQuantityValidation } from './useProductQuantityValidation';

import {
  ProductQuantityInputOptions,
  SelectedOption,
} from '../types/ProductPageTypes';

export const useProductQuantityInput = (
  selectedOptions: SelectedOption[],
  options: ProductQuantityInputOptions,
) => {
  const { parseQuantityInput } = useProductQuantityValidation();
  const {
    onQuantityChange,
    onValidationError = alert,
    debounceDelay = 1000,
  } = options;

  const [inputValues, setInputValues] = useState<Record<number, string>>({});
  const debounceRefs = useRef<Record<number, NodeJS.Timeout>>({});

  const handleInputChange = useCallback(
    (index: number, inputValue: string) => {
      const selectedOption = selectedOptions[index];
      if (!selectedOption) return;

      setInputValues((prev) => ({
        ...prev,
        [index]: inputValue,
      }));

      if (debounceRefs.current[index]) {
        clearTimeout(debounceRefs.current[index]);
      }

      debounceRefs.current[index] = setTimeout(() => {
        const validation = parseQuantityInput(selectedOption, inputValue);

        if (!validation.isValid && validation.message) {
          onValidationError(validation.message);
        }

        const finalQuantity = validation.validQuantity;

        setInputValues((prev) => ({
          ...prev,
          [index]: finalQuantity.toString(),
        }));

        onQuantityChange(index, finalQuantity);
      }, debounceDelay);
    },
    [
      selectedOptions,
      parseQuantityInput,
      onQuantityChange,
      onValidationError,
      debounceDelay,
    ],
  );

  const getInputValue = useCallback(
    (index: number, currentQuantity: number): string => {
      return inputValues[index] ?? currentQuantity.toString();
    },
    [inputValues],
  );

  const resetInputValue = useCallback((index: number, quantity: number) => {
    setInputValues((prev) => ({
      ...prev,
      [index]: quantity.toString(),
    }));
  }, []);

  useEffect(() => {
    const timeouts = debounceRefs.current;

    return () => {
      Object.values(timeouts).forEach(clearTimeout);
    };
  }, []);

  return {
    handleInputChange,
    getInputValue,
    resetInputValue,
  };
};
